import type { SpinResponse } from "@slot/client-engine";
import type { CoreServices } from "../CoreServices";
import type { GameRuntime } from "../../composition/GameRuntime";
import type { ReplayRoundProvider } from "../../replay/ReplayRoundProvider";
import type { BootLoaderPort } from "../loader/BootLoaderPort";
import type { SystemModalPresenter } from "../../system-modal/SystemModalPresenter";
import { failureModal, flowFailure } from "../../system-modal/failureCopy";
import type { Log } from "@slot/presentation";

export type ReplayStatus = "fetching" | "fetch_failed" | "playing" | "complete" | "playback_failed";

/** The fetched replay round, or the failure; `status` is what the debug hook reports. */
export interface ReplayState {
  status: ReplayStatus;
  spinResponse: SpinResponse | null;
}

/**
 * `?replay=<id>`, first half: the round, fetched before the services exist, so the money can be the round's currency
 * (decision 81: a public replay carries the round's `currencyCode`, and the viewer's own settings are anonymous). A
 * failed fetch is reported once the UI is up (the modal needs the scene).
 */
export async function fetchReplayRound(roundId: string, replayRounds: ReplayRoundProvider, loader: BootLoaderPort | null, log: Log): Promise<ReplayState> {
  log(`[Replay] replay mode — roundId="${roundId}"`);
  loader?.setStatusText("Fetching replay...");
  loader?.setProgress01(0.85);
  try {
    return { status: "fetching", spinResponse: await replayRounds.fetchRoundForReplay(roundId) };
  } catch (err) {
    log(`[Replay] replay fetch failed: ${err instanceof Error ? err.message : String(err)}`);
    return { status: "fetch_failed", spinResponse: null };
  }
}

/**
 * The replayed round's bet on the bet panel, through the display override (the round may not be a step in the viewer's
 * ladder), and, for a title with lines, the lines it covered (decision 56).
 */
export function showReplayBet(replay: ReplayState, services: Pick<CoreServices, "betSystem">): void {
  const firstEvent = replay.spinResponse?.roundSnapshot.orderedEvents[0];
  if (firstEvent?.betAmountAtEvent) services.betSystem.setDisplayOverride(firstEvent.betAmountAtEvent, firstEvent.betLinesAtEvent);
}

/**
 * Second half: the round plays through the engine directly — no coordinator, no settlement, no
 * `presentation-complete` — and freezes on its final state. The boot fallback grid is cleared by the
 * round's own first `spin_boundary_start`. Either failure shows the replay-failed modal, whose only
 * offer is a reload.
 */
export async function playReplayRound(
  replay: ReplayState,
  runtime: GameRuntime,
  services: Pick<CoreServices, "localization">,
  systemModalPresenter: SystemModalPresenter,
  reloadPage: () => void,
  log: Log,
): Promise<void> {
  const showFailure = (): void => {
    void systemModalPresenter.show(failureModal(flowFailure("replay"), (key, fallback) => services.localization.t(key, fallback))).then(() => reloadPage());
  };
  if (!replay.spinResponse) {
    showFailure();
    return;
  }
  replay.status = "playing";
  try {
    await runtime.playReplayRound(replay.spinResponse);
    replay.status = "complete";
    log("[Replay] replay complete — frozen on final state");
  } catch (err) {
    replay.status = "playback_failed";
    log(`[Replay] replay playback failed: ${err instanceof Error ? err.message : String(err)}`);
    showFailure();
  }
}
